import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Trash2, Clock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  sent: "Odoslané — čaká", replied: "Odpovedali", meeting: "Stretnutie", declined: "Odmietli", partner: "Partner / ambasádor",
};
const FOLLOW_DAYS = 10;

export default function OutreachList({ token, refreshKey }) {
  const [list, setList] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/outreach`, auth); setList(data); } catch { setList([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const patch = async (o, upd) => {
    setList((l) => l.map((x) => (x.id === o.id ? { ...x, ...upd } : x)));
    try { await axios.patch(`${API}/admin/outreach/${o.id}`, upd, auth); } catch { load(); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Odstrániť záznam ${o.brand}?`)) return;
    await axios.delete(`${API}/admin/outreach/${o.id}`, auth); load();
  };

  if (!list) return null;
  const days = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

  return (
    <div className="mb-12" data-testid="outreach-list">
      <h3 className="font-display text-lg mb-1">Prehľad oslovených značiek ({list.length})</h3>
      <p className="text-white/40 text-sm mb-4">Zapisuje sa automaticky pri odoslaní šablóny. Odpoveď z rovnakej firmy sa rozpozná sama; po {FOLLOW_DAYS} dňoch bez odpovede uvidíte „Ozvať sa znova".</p>
      {list.length === 0 ? (
        <p className="text-white/35 text-sm border border-white/10 p-4" data-testid="outreach-empty">Zatiaľ ste neoslovili žiadnu značku.</p>
      ) : (
        <div className="space-y-2">
          {list.map((o) => {
            const replied = !!o.reply_at;
            const follow = o.status === "sent" && !replied && days(o.sent_at) >= FOLLOW_DAYS;
            return (
              <div key={o.id} data-testid={`outreach-row-${o.id}`}
                className={`border p-4 flex flex-col lg:flex-row lg:items-center gap-3 ${follow ? "border-amber-400/50 bg-amber-400/5" : "border-white/10 bg-[#0a0a0a]"}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-semibold">{o.brand} <span className="text-white/40 font-normal text-sm">· {o.email} · {(o.lang || "sk").toUpperCase()}</span></p>
                  <p className="text-white/45 text-xs mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>Oslovené {new Date(o.sent_at).toLocaleDateString("sk-SK")} ({days(o.sent_at)} dní)</span>
                    {replied && <span className="text-emerald-300 inline-flex items-center gap-1" data-testid="outreach-replied"><CheckCircle2 size={12} /> Odpoveď {new Date(o.reply_at).toLocaleDateString("sk-SK")} od {o.reply_from}</span>}
                    {follow && <span className="text-amber-300 inline-flex items-center gap-1" data-testid="outreach-follow"><Clock size={12} /> Ozvať sa znova</span>}
                  </p>
                  <input defaultValue={o.note} onBlur={(e) => e.target.value !== (o.note || "") && patch(o, { note: e.target.value })} placeholder="Poznámka (kontakt, dohoda…)"
                    data-testid="outreach-note" className="mt-2 w-full bg-black border border-white/10 px-3 py-1.5 text-xs text-white focus:border-[#D4AF37] outline-none" />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select value={o.status} onChange={(e) => patch(o, { status: e.target.value })} data-testid="outreach-status"
                    className="bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none">
                    {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button onClick={() => remove(o)} data-testid="outreach-delete" className="w-9 h-9 flex items-center justify-center text-white/40 hover:text-red-400"><Trash2 size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
